import { useCallback, useEffect, useState } from 'react';

import isMatchWith from 'lodash/isMatchWith';

import { simpleHash } from '@atlaskit/frontend-utilities/simple-hash';

import { usePostOfficeContext } from '../post-office-context';
import { type PostOfficeContextValue } from '../post-office-context/types';
import type { Callback, Matcher } from '../post-office-gasv3-listener/store/types';
import { usePostOfficeRoute } from '../post-office-route/store';
import { type RouteState } from '../post-office-route/store/types';
import { createSimplePubSub } from '../util/simple-pub-sub';
import type { Publisher, Subscriber } from '../util/simple-pub-sub/types';

import type {
	ActiveScopeEntry,
	ContextMatch,
	MatchOptions,
	PostOfficeActiveScopes,
	PostOfficeScopeConfig,
	RouteMatch,
	ScopeEntry,
	UsePostOfficeContextScope,
} from './types';

const scopesNamesAsString = <T extends { name: string }>(scopes: Array<T> = []): string =>
	scopes
		.map((e) => e.name)
		.sort()
		.join(',');

export const usePostOfficeContextScope: UsePostOfficeContextScope = (config) => {
	const currentScopeNames = scopesNamesAsString(config.scopes);

	const matchScopes = useCallback(
		(input: { route: RouteState | RouteMatch; context: PostOfficeContextValue }) =>
			createMatchScopes(config)(input),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[currentScopeNames],
	);

	const [state, setState] = useState(initialState);

	const route = usePostOfficeRoute();

	const context = usePostOfficeContext();

	const next = matchScopes({ route, context });
	const nextScopeNames = scopesNamesAsString(next);
	const updateKey = `${currentScopeNames}-${nextScopeNames}`;

	usePostOfficeScopeSpy(state, config);

	useEffect(() => {
		if (nextIsDifferent({ next, state, treatIntersectionAsSame: config.treatIntersectionAsSame })) {
			setState(({ current }) => ({
				updateKey: simpleHash(updateKey),
				current: next,
				previous: current,
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateKey]);

	return state;
};

const initialState: PostOfficeActiveScopes = {
	updateKey: undefined,
	current: [],
	previous: [],
};

// Match Scope

type CreateMatchScope = (context: {
	route: RouteState | RouteMatch;
	context: PostOfficeContextValue;
}) => (input: ScopeEntry) => boolean;
const createMatchScope: CreateMatchScope =
	({ route, context }) =>
	(input: ScopeEntry): boolean => {
		const contextMatch: ContextMatch = route
			? 'pathname' in route
				? { route, context }
				: route.current
					? { route: extractRelevantUrlFields(route.current), context }
					: { route: undefined, context }
			: { route: undefined, context };

		const inputMatch = input.when;

		return matchScope({ contextMatch, inputMatch });
	};

const extractRelevantUrlFields = (route: string): RouteMatch => {
	const { hostname, pathname, search } = new URL(route);

	return { hostname, pathname, search };
};

// Match Context

type ValueMatchParams = {
	contextValue?: string | Record<string, unknown>;
	inputValue?: MatchOptions | Record<string, MatchOptions>;
};

export const matchScope = ({
	contextMatch,
	inputMatch,
}: {
	contextMatch: Record<string, unknown>;
	inputMatch: Record<string, unknown>;
}): boolean =>
	isMatchWith(
		contextMatch,
		inputMatch,
		(contextValue: ValueMatchParams['contextValue'], inputValue: ValueMatchParams['inputValue']) =>
			isValueMatchDeep({ contextValue, inputValue }),
	);

const isValueMatch = ({ contextValue, inputValue }: ValueMatchParams): boolean => {
	if (!contextValue || !inputValue) {
		return false;
	}

	if (typeof inputValue === 'string') {
		return contextValue === inputValue;
	}

	if (inputValue?.equals) {
		return contextValue === inputValue.equals;
	}

	if (!inputValue?.matches) {
		return false;
	}

	if (typeof inputValue.matches !== 'string' || typeof contextValue !== 'string') {
		return false;
	}

	return new RegExp(inputValue.matches).test(contextValue);
};

const isValueMatchDeep = ({ contextValue, inputValue }: ValueMatchParams): boolean =>
	typeof inputValue === 'object' && typeof contextValue === 'object'
		? matchScope({ contextMatch: contextValue, inputMatch: inputValue })
		: isValueMatch({ contextValue, inputValue });

type CreateMatchScopes = (
	config: PostOfficeScopeConfig,
) => (input: {
	route: RouteState | RouteMatch;
	context: PostOfficeContextValue;
}) => Array<ActiveScopeEntry>;
export const createMatchScopes: CreateMatchScopes =
	({ scopes }) =>
	(input) =>
		scopes.filter(createMatchScope(input)).map((scope) => ({ name: scope.name }));

type NextIsDifferent = (params: {
	next: Array<ActiveScopeEntry>;
	state: PostOfficeActiveScopes;
	treatIntersectionAsSame?: boolean;
}) => boolean;
export const nextIsDifferent: NextIsDifferent = ({ next, state, treatIntersectionAsSame }) => {
	const currentSet = Array.from(new Set(state.current.map((e) => e.name)));
	const nextSet = Array.from(new Set(next.map((e) => e.name)));

	if (treatIntersectionAsSame) {
		if (currentSet.length !== 0 && nextSet.length === 0) {
			return true;
		}
	} else {
		if (currentSet.length !== nextSet.length) {
			return true;
		}
	}

	return !nextSet.every((e) => currentSet.includes(e));
};

// Scope Spy

const usePostOfficeScopeSpy = (state: PostOfficeActiveScopes, config: PostOfficeScopeConfig) => {
	if (config?.consoleSpy && typeof window !== 'undefined' && !window?.postOffice?.scopes) {
		const [listen, update] = createSimplePubSub<PostOfficeActiveScopes>();

		window.postOffice = {
			...window.postOffice,
			scopes: {
				[config?.consoleSpy.name]: {
					listen,
					update,
					list: config.scopes,
				},
			},
		};
	}

	useEffect(() => {
		if (!config?.consoleSpy) {
			return;
		}

		if (!window.postOffice?.scopes) {
			return;
		}

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		window.postOffice?.scopes?.[config?.consoleSpy.name]?.update?.(state);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scopesNamesAsString(state.current)]);
};

declare global {
	interface Window {
		postOffice?: {
			gasv3Listener?: {
				subscribers: Map<Matcher, Callback>;
			};
			scopes?: {
				[K: string]: {
					update: Publisher<PostOfficeActiveScopes>;
					listen: Subscriber<PostOfficeActiveScopes>;
					list: PostOfficeScopeConfig['scopes'];
				};
			};
		};
	}
}
